// ============================================================================
// FORMATO REGIONAL — CABLEADO (token → app)
// ============================================================================
// PUNTO ÚNICO donde el Formato Regional del proyecto pasa de ser un dato en el
// token a estar efectivamente aplicado en la aplicación.
//
// Antes de este módulo, la cadena estaba completa salvo el último eslabón:
// la columna `proyectos.locale` se guardaba bien, viajaba en el JWT dentro de
// `proyectos[].locale`, y `getProjectLocaleConfig()` sabía leerla... pero
// NADIE la llamaba, y `setGlobalLocale()` nunca se invocaba. `activeLocale`
// quedaba fijo para todos los proyectos.
//
// Qué toca applyProjectLocale()
//   1. formatProjectData.setGlobalLocale() → separador decimal y de miles
//   2. dayjs.locale()                      → nombres de mes y día
//   3. Highcharts.lang                     → separadores y meses/días en gráficos
//   4. <html lang="...">                   → accesibilidad / lectores de pantalla
//   5. evento 'hb:locale-changed'          → para quien necesite reaccionar
//
// Cómo se mantiene sincronizado
//   - main.js llama applyProjectLocale() + installLocaleSync() al arrancar.
//   - installLocaleSync() escucha 'hb:session-updated', que YA disparan todos
//     los caminos de selección de proyecto (Login, Welcome, stores/proyecto,
//     sincronización entre pestañas). Por eso no hace falta ensuciar los 8
//     puntos que escriben `proyectSelected`.
//   - Los caminos que cambian el proyecto SIN disparar ese evento (App.vue y
//     el hidratado de proyecto único del router) llaman a applyProjectLocale()
//     de forma explícita.
//
// Próxima fase: acá se enganchará también el catálogo de formatos de fecha
// (DD/MM/YYYY vs MM/DD/YYYY, 24h vs 12h), que hoy sigue hardcodeado en los
// componentes.

import dayjs from './dayjs';
import { getProjectLocaleConfig, getProjectTimezoneConfig } from './auth';
import { setGlobalLocale } from './formatProjectData';
import { setProjectTimezone } from './formatProjectDate';
import { DEFAULT_LOCALE, dayjsLocaleFor, resolveLocale } from './localeConfig';
import { applyHighchartsLocale } from './highchartsLocale';
import { refrescarMeses } from '../composables/useDemandaRiego';

/** Último locale aplicado, para no repetir trabajo ni emitir eventos de más. */
let appliedLocale = null;

/**
 * Aplica un locale concreto a todas las capas de presentación.
 * Idempotente: si ya está aplicado, no hace nada.
 *
 * @param {string} locale       BCP-47 soportado ('es-CL' | 'en-US')
 * @param {boolean} [force]     re-aplica aunque no haya cambiado
 * @returns {string} el locale efectivamente aplicado
 */
export function applyLocale(locale, { force = false } = {}) {
  const target = resolveLocale(locale);

  // El huso se aplica ANTES del corto-circuito de idempotencia, y a propósito.
  // Locale y huso son ejes INDEPENDIENTES: dos proyectos pueden compartir
  // 'es-CL' y estar en husos distintos (Chile y Magallanes, o Chile y Denver).
  // Si esto fuera después del `return`, cambiar a un proyecto con el mismo
  // locale dejaría el huso anterior aplicado y las fechas quedarían corridas
  // sin ninguna señal.
  setProjectTimezone(getProjectTimezoneConfig().timezone);

  if (!force && target === appliedLocale) return appliedLocale;

  // 1. Números (Intl.NumberFormat): separador decimal y de miles.
  setGlobalLocale(target);

  // 2. Fechas (Day.js): nombres de mes y día. El ORDEN de los campos
  //    ('DD/MM/YYYY' vs 'MM/DD/YYYY') no lo decide Day.js sino el catálogo de
  //    utils/formatProjectDate.js.
  dayjs.locale(dayjsLocaleFor(target));

  // 3. Gráficos (Highcharts.lang): separador decimal/de miles y nombres de mes
  //    y día. Highcharts no usa Intl, así que necesita su propia configuración.
  //    Es no-op mientras no haya una pantalla de gráficos cargada; el propio
  //    componente la aplica al registrarse (utils/highchartsLocale.js).
  applyHighchartsLocale(target);

  // 3 bis. Nombres de mes abreviados que se usan como CATEGORÍAS de eje
  //        (gráfico de demanda). No pasan por Highcharts ni por Day.js:
  //        son strings que el código arma antes de dibujar.
  refrescarMeses(target);

  // 4. Atributo lang del documento (accesibilidad y hyphenation del navegador).
  try {
    if (typeof document !== 'undefined' && document.documentElement) {
      document.documentElement.setAttribute('lang', target);
    }
  } catch {
    // no-op (SSR / entorno sin DOM)
  }

  const previous = appliedLocale;
  appliedLocale = target;

  // 5. Aviso para quien necesite re-dibujar algo imperativo (gráficos ya
  //    renderizados, DataTables, instancias de flatpickr ya montadas).
  try {
    if (typeof window !== 'undefined' && previous !== target) {
      window.dispatchEvent(new CustomEvent('hb:locale-changed', {
        detail: { locale: target, previous }
      }));
    }
  } catch {
    // no-op
  }

  return appliedLocale;
}

/**
 * Lee el Formato Regional del proyecto activo (override local → token →
 * default) y lo aplica. Es la función que hay que llamar cada vez que cambia
 * el proyecto seleccionado o la sesión.
 *
 * @returns {string} el locale aplicado
 */
export function applyProjectLocale(options = {}) {
  let config;
  try {
    config = getProjectLocaleConfig();
  } catch {
    // Sin token o sessionStorage inaccesible: el default mantiene la app usable.
    config = { locale: DEFAULT_LOCALE, configured: false };
  }
  return applyLocale(config.locale, options);
}

/** Locale actualmente aplicado (null si todavía no se aplicó ninguno). */
export function getAppliedLocale() {
  return appliedLocale;
}

/**
 * Suscribe la re-aplicación automática del locale a los eventos de sesión.
 * Se llama UNA vez desde main.js. Devuelve la función para desuscribir.
 */
export function installLocaleSync() {
  if (typeof window === 'undefined') return () => {};

  const handler = () => applyProjectLocale();

  // Disparado por Login, Welcome, stores/proyecto y la sincronización entre
  // pestañas de utils/auth.js cada vez que cambia el proyecto o la sesión.
  window.addEventListener('hb:session-updated', handler);

  return () => window.removeEventListener('hb:session-updated', handler);
}

export default {
  applyLocale,
  applyProjectLocale,
  getAppliedLocale,
  installLocaleSync
};
